import type { ReactNode } from 'react';
import { Box } from '@mui/material';

/**
 * Shared 16:9 preview frame used by Now and Next so both match in size.
 * Overlays (labels, play) render above layer content via a dedicated stacking layer.
 */
export function FlowControlPreviewFrame({
  children,
  overlay,
  borderColor,
  borderWidth = 1,
}: {
  children: ReactNode;
  overlay?: ReactNode;
  /** Optional border color (e.g. `error.main` for Now, `success.main` for Next). */
  borderColor?: string;
  /** Theme border width units (Now/Next use 2 — double the default frame). */
  borderWidth?: number;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        containerType: 'size',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: 'min(100cqw, calc(100cqh * 16 / 9))',
          aspectRatio: '16 / 9',
          flexShrink: 0,
          position: 'relative',
          bgcolor: '#000',
          borderRadius: 1,
          overflow: 'hidden',
          border: borderWidth,
          borderColor: borderColor ?? ((theme) => (theme.palette.mode === 'dark' ? 'grey.600' : 'grey.400')),
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>{children}</Box>
        {overlay ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 30,
              pointerEvents: 'none',
            }}
          >
            {overlay}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
